import React, { useEffect, useState } from 'react';
import { IMediaData } from '../utils/helper';
import { IGridSettings, IImageSettings, Image } from '@msdyn365-commerce/core';
import ModalBody from 'reactstrap/lib/ModalBody';
import { Modal } from 'reactstrap';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy';
import { enablePinchToZoomOnThisDiv } from '../utils/zoom-utils';
import Button from 'reactstrap/lib/Button';
import { INodeProps, SingleSlideCarousel, Node, IComponentNodeProps } from '@msdyn365-commerce-modules/utilities';
import { PanZoom } from 'panzoom';
import { Carousel, ICarouselProps } from '@msdyn365-commerce/components';
import { _getScreenSize, cleanupScreenSizeListener } from '../../../themes/dobbies/utilities/display-utils';

export interface IDobbiesMediaGalleryZoomedViewProps {
    images: IMediaData[];
    currIndex: number;
    gridSettings: IGridSettings;
    shouldRenderZoomedView: boolean;
    productAttributes: AttributeValue[];
    onViewClosed(): void;
}

export const DobbiesMediaGalleryZoomedView: React.FC<IDobbiesMediaGalleryZoomedViewProps> = (
    props: IDobbiesMediaGalleryZoomedViewProps
) => {
    const [currImageIndex, setCurrImageIndex] = useState(0);
    const [shouldShowModal, setShoulShowModal] = useState(false);
    // @ts-ignore
    const [animating, setAnimating] = useState(false);
    const [panzoom, setPanZoom] = useState<PanZoom | null>(null);
    const [zoomedIn, setZoomedIn] = useState<boolean>(false);
    const [showZoomButton, setShowZoomButton] = useState<boolean>(false);
    const [screenSize, setScreenSize] = useState<number>(3);

    const resetPanZoom = () => {
        if (panzoom) {
            panzoom.moveTo(0, 0);
            panzoom.zoomAbs(0, 0, 1);
            panzoom.dispose();
        }
    };

    useEffect(() => {
        setCurrImageIndex(props.currIndex);
        setShoulShowModal(props.shouldRenderZoomedView);
        const device = _getScreenSize();
        setScreenSize(device);

        return () => {
            cleanupScreenSizeListener();
        };
    }, []);

    useEffect(() => {
        setTimeout(() => {
            resetPanZoom();
            const panzoomInstance = enablePinchToZoomOnThisDiv('main-zoomed-img');
            setPanZoom(panzoomInstance);
            setZoomedIn(false);
        }, 800);
        setShoulShowModal(props.shouldRenderZoomedView);
    }, [props.shouldRenderZoomedView, currImageIndex]);

    useEffect(() => {
        // Check if screen size is desktop, then disable default panzoom
        if (screenSize >= 3) {
            setShowZoomButton(true);
            panzoom?.pause();
        } else {
            setShowZoomButton(false);
            panzoom?.resume();
        }
    }, [screenSize, panzoom]);

    useEffect(() => {
        setCurrImageIndex(props.currIndex);
    }, [props.currIndex]);

    const zoomIn = () => {
        setZoomedIn(true);
        const img = document.getElementById('main-zoomed-img');
        const container = document.querySelector('.msc-carousel__item.active');
        const containerX = document.querySelector('.ms-media-gallery__carousel');
        if (!img || !panzoom || !container) {
            return;
        }

        const w = containerX?.clientWidth;
        const h = containerX?.clientHeight;

        panzoom.resume();

        panzoom.smoothZoom(w! / 2, h! / 2, 3);

        requestAnimationFrame(() => {
            panzoom.moveTo(0, 0);
        });
    };

    const zoomOut = () => {
        if (!panzoom) {
            return;
        }

        setZoomedIn(false);

        panzoom.smoothZoomAbs(0, 0, 1);

        panzoom.pause();
    };

    if (!props.shouldRenderZoomedView) {
        return null;
    }

    const productFeatureAttr = props.productAttributes.find(attr => attr.Name === 'Product Feature Text');

    const ProductImageContainer: INodeProps = {
        tag: 'li',
        className: `ms-product-collection__item msc-active-shadow`,
        role: 'listitem'
    };

    const defaultThumbnailImageSettings: IImageSettings = {
        viewports: {
            xs: { q: 'w=100&m=8', w: 100, h: 100 },
            lg: { q: 'w=100&m=8', w: 100, h: 100 },
            xl: { q: 'w=100&m=8', w: 100, h: 100 }
        },
        cropFocalRegion: true
    };

    const isFirstItem = () => currImageIndex === 0;

    const isLastItem = () => {
        const images = props.images;
        return images && currImageIndex === images.length - 1;
    };

    const goToIndex = (index: number) => {
        setCurrImageIndex(index);
    };

    const onNext = () => {
        const nextIndex = isLastItem() ? 0 : currImageIndex + 1;
        goToIndex(nextIndex);
        setZoomedIn(false);
    };

    const onPrevious = (): void => {
        const images = props.images;
        const nextIndex = isFirstItem() ? (images ? images.length - 1 : 0) : currImageIndex - 1;
        goToIndex(nextIndex);
        setZoomedIn(false);
    };

    const onExiting = () => {
        setAnimating(true);
    };

    const onExited = () => {
        setAnimating(false);
    };

    const getCarouselItem = (img: IMediaData, index: number) => {
        return (
            <Image
                className='ms-media-gallery__item__curritem'
                {...img}
                gridSettings={props.gridSettings}
                loadFailureBehavior='hide'
                id={currImageIndex === index ? `main-zoomed-img` : ''}
            />
        );
    };

    const getThumbnailItem = (img: IMediaData, index: number) => {
        return (
            <Node {...ProductImageContainer} key={`${img.altText}-${index}`}>
                <Image
                    className={`${index === currImageIndex ? 'active' : ''}`}
                    {...img}
                    gridSettings={props.gridSettings}
                    imageSettings={defaultThumbnailImageSettings}
                    loadFailureBehavior='hide'
                    id={`zoomed__carousel-item__${currImageIndex}`}
                    key={index}
                    onClick={() => {
                        setCurrImageIndex(index);
                    }}
                />
            </Node>
        );
    };

    const carouselprops = {
        tag: Carousel,
        className: 'ms-media-gallery__carousel',
        items:
            props.images &&
            props.images.map((img, index) => {
                return getCarouselItem(img, index);
            }),
        activeIndex: currImageIndex,
        next: onNext,
        previous: onPrevious,
        interval: false,
        directionTextPrev: 'Previous',
        directionTextNext: 'Next',
        onIndicatorsClickHandler: goToIndex,
        showPaginationTooltip: false,
        hideIndicator: false,
        keyboard: false,
        handleOnExited: onExited,
        handleOnExiting: onExiting
    } as IComponentNodeProps<ICarouselProps>;

    return (
        <>
            {props.images.length ? (
                <Modal isOpen={shouldShowModal} className='pdp-zoom-modal fullscreen-modal'>
                    <ModalBody>
                        <Button
                            className='zoom-close'
                            onClick={() => {
                                setShoulShowModal(false);
                                props.onViewClosed();
                            }}
                        ></Button>
                        <p className='pinch-zoom-title'>Pinch to zoom in/out</p>
                        <Node {...carouselprops} />
                        <div className='thumbnail-images'>
                            {showZoomButton && (
                                <button
                                    className={`zoom-btn ${zoomedIn ? 'zoomed-in' : ''}`}
                                    onClick={zoomedIn ? zoomOut : zoomIn}
                                ></button>
                            )}
                            <SingleSlideCarousel>
                                {props.images &&
                                    props.images.map((img, index) => {
                                        return getThumbnailItem(img, index);
                                    })}
                            </SingleSlideCarousel>
                            {productFeatureAttr?.TextValue ? <p>{productFeatureAttr?.TextValue}</p> : ''}
                        </div>
                    </ModalBody>
                </Modal>
            ) : (
                <></>
            )}
        </>
    );
};
